import { Injectable } from '@angular/core';
import { HotelSearchRequest } from '@modules/hotel-booking/models/hotel-search.model';
import { DetailHotel } from '@modules/hotel-booking/models/view-details';
import { BehaviorSubject, Observable } from 'rxjs';


@Injectable({
  providedIn: 'root',
})
export class HotelDetailsService {

  private _hotelURL: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  hotelURL$: Observable<any> = this._hotelURL.asObservable();
  
  private _cacheHotel: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  cacheHotel$: Observable<any> = this._cacheHotel.asObservable();

  private cacheMapHotel: Map<string, { hotel: any; timestamp: number }> = new Map();

  private cacheMapRooms: Map<string, { rooms: any; timestamp: number }> = new Map();

  private cacheMapSearch: Map<string, { searchData: any; timestamp: number }> = new Map();

  private cacheCheckInterval: number | null = null;
  
  private _notiSearchResult: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  notiSearchResult$: Observable<any> = this._notiSearchResult.asObservable();

  constructor() {
    // Start interval to check and clear expired cache every 5 minutes
    this.startCacheCleanup();
  }

  isFacilities(hotel: DetailHotel): boolean {
    if(!hotel) return false;
    return hotel.property_amenity_list?.length > 0;
  }

  isDescription(hotel: DetailHotel): boolean {
    if(!hotel) return false;
    return hotel.property_description?.length > 0;
  }

  setHotelURL(data: any) {    
    this._hotelURL.next(data);
  }

  getHotelURL() {
    return this.hotelURL$;
  }

  getCacheHotel(propertyId: string): any | null {
    const cacheEntryString = sessionStorage.getItem(`hotel_${propertyId}`);
    if (!cacheEntryString) {
      return null;
    }

    try {
      const cacheEntry = JSON.parse(cacheEntryString);
      const currentTime = Date.now();
      const cacheDuration = 7 * 24 * 60 * 1000; // 30 minutes

      if (currentTime - cacheEntry.timestamp > cacheDuration) {
        sessionStorage.removeItem(`hotel_${propertyId}`);
        return null;
      }

      return cacheEntry.hotel;
    } catch (e) {
      console.error('Error parsing cache entry from sessionStorage:', e);
      sessionStorage.removeItem(`hotel_${propertyId}`);
      return null;
    }
  }

  setCacheHotel(propertyId: string, hotel: any) {
    const timestamp = Date.now();
    const cacheEntry = { hotel, timestamp };
    try {
      sessionStorage.setItem(`hotel_${propertyId}`, JSON.stringify(cacheEntry));
    } catch (e) {
      console.error('Error saving to sessionStorage:', e);
    }
  }

  private generateCacheKey(
    propertyId: string,
    checkIn: string,
    checkOut: string,
    adults: string | number,
    children: string | number,
    childAges: string[],
    rooms: string | number
  ): string {
    const childAgesKey = childAges.join('_');
    return `${propertyId}_${checkIn}_${checkOut}_${adults}_${children}_${childAgesKey}_${rooms}`;
  }

  setCacheRooms(
    propertyId: string,
    checkIn: string,
    checkOut: string,
    adults: string | number,
    children: string | number,
    childAges: string[],
    rooms: string | number,
    roomData: any
  ): void {
    const key = this.generateCacheKey(propertyId, checkIn, checkOut, adults, children, childAges || [], rooms);

    const timestamp = Date.now();
    this.cacheMapRooms.set(key, { rooms: roomData, timestamp });
  }

  clearRoom(
    propertyId: string,
    checkIn: string,
    checkOut: string,
    adults:  number,
    children: number,
    childAges: string[],
    roomsKey: number
  ) {
    const key = this.generateCacheKey(propertyId, checkIn, checkOut, adults, children, childAges || [], roomsKey);
    this.cacheMapRooms.delete(key);
  }
  
  getCacheRooms(
    propertyId: string,
    checkIn: string,
    checkOut: string,
    adults:  number,
    children: number,
    childAges: string[],
    roomsKey: number
  ): any | null {
    const key = this.generateCacheKey(propertyId, checkIn, checkOut, adults, children, childAges || [], roomsKey);
    const cacheEntry = this.cacheMapRooms.get(key);

    if (!cacheEntry) return null;

    const { rooms, timestamp } = cacheEntry;
    const currentTime = Date.now();
    const cacheDuration = 30 * 60 * 1000; // 30 minutes

    if (currentTime - timestamp > cacheDuration) {
      this.cacheMapRooms.delete(key);
      return null;
    }

    return rooms;
  }

  private generateCacheKeySearch(params: HotelSearchRequest): string {
    const {
      checkin,
      checkout,
      page_num,
      page_size,
      destination,
      dest_type,
      country_code,
      num_of_rooms,
      num_of_children,
      num_of_adults,
      child_ages_list,
      min_price,
      max_price,
      property_rating_list,
      accompanying_service_list,
      property_types_list,
      distance_from_center_list,
      convenient_list,
      review_score_list,
      sorting,
      latitude,
      longitude,
      destination_name,
    } = params;

    // Only include core parameters in the key and ensure other parameters are empty or null
    if (
      min_price ||
      max_price ||
      property_rating_list ||
      accompanying_service_list ||
      property_types_list ||
      distance_from_center_list ||
      convenient_list ||
      review_score_list ||
      // sorting ||
      latitude ||
      longitude      
    ) {
      return ''; // Return empty string if non-core parameters are present
    }

    const keyParts = [
      checkin || '',
      checkout || '',
      page_num || '',
      page_size || '',
      destination || '',
      dest_type || '',
      country_code || '',
      num_of_rooms || '',
      num_of_children || '',
      num_of_adults || '',
      child_ages_list || '',
    ];

    return keyParts.join('|');
  }

  setCacheSearch(searchData: any, params: HotelSearchRequest) {
    const key = this.generateCacheKeySearch(params);
    if (!key) {
      return; // Don't cache if key is invalid
    }

    const timestamp = Date.now();
    const cacheEntry = { searchData, timestamp };

    try {
      localStorage.setItem(`hotelSearch_${key}`, JSON.stringify(cacheEntry));
    } catch (e) {
      console.error('Error saving to localStorage:', e);
    }
  }

  getCacheSearch(params: HotelSearchRequest): any | null {
    const key = this.generateCacheKeySearch(params);
    if (!key) {
      return null; // Return null if key is invalid
    }

    try {
      const cacheEntry = localStorage.getItem(`hotelSearch_${key}`);
      if (!cacheEntry) {
        return null;
      }

      const { searchData, timestamp } = JSON.parse(cacheEntry);
      const currentTime = Date.now();
      const cacheDuration = 10 * 60 * 1000; // 10 minutes

      if (currentTime - timestamp > cacheDuration) {
        localStorage.removeItem(`hotelSearch_${key}`);
        return null;
      }

      return searchData;
    } catch (e) {
      console.error('Error reading from localStorage:', e);
      return null;
    }
  }

  clearCacheByDestination(destination: string): void {
    for (const [key, value] of this.cacheMapSearch) {
      const params = this.parseCacheKeySearch(key);
      if (params?.destination === destination) {
        this.cacheMapSearch.delete(key);
      }
    }
  }

  private parseCacheKeySearch(key: string): HotelSearchRequest | null {
    const keyParts = key.split('|');
    if (keyParts.length !== 23) {
      return null; // Số lượng phần không khớp
    }

    return {
      checkin: keyParts[0] || '',
      checkout: keyParts[1] || '',
      page_num: keyParts[2] ? Number(keyParts[2]) : undefined,
      page_size: keyParts[3] ? Number(keyParts[3]) : undefined,
      destination: keyParts[4] || '',
      dest_type: keyParts[5] || '',
      country_code: keyParts[6] || '',
      num_of_rooms: keyParts[7] ? Number(keyParts[7]) : 1,
      num_of_children: keyParts[8] ? Number(keyParts[8]) : 0,
      num_of_adults: keyParts[9] ? Number(keyParts[9]) : 1,
      child_ages_list: keyParts[10] || undefined,
      min_price: keyParts[11] ? Number(keyParts[11]) : undefined,
      max_price: keyParts[12] ? Number(keyParts[12]) : undefined,
      property_rating_list: keyParts[13] ? JSON.parse(keyParts[13]) : undefined,
      accompanying_service_list: keyParts[14] ? JSON.parse(keyParts[14]) : undefined,
      property_types_list: keyParts[15] ? JSON.parse(keyParts[15]) : undefined,
      distance_from_center_list: keyParts[16] ? JSON.parse(keyParts[16]) : undefined,
      convenient_list: keyParts[17] ? JSON.parse(keyParts[17]) : undefined,
      review_score_list: keyParts[18] ? JSON.parse(keyParts[18]) : undefined,
      sorting: keyParts[19] || undefined,
      latitude: keyParts[20] || undefined,
      longitude: keyParts[21] || undefined,
      destination_name: keyParts[22] || undefined,
      property_id: keyParts[4].startsWith('prop_') ? keyParts[4] : undefined,
      currency: ''
    };
  }

  private clearExpiredCache() {
    const cacheDuration = 10 * 60 * 1000; // 10 minutes
    const currentTime = Date.now();

    try {
      // Iterate through all localStorage keys
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && key.startsWith('hotelSearch_')) {
          const cacheEntry = localStorage.getItem(key);
          if (cacheEntry) {
            const { timestamp } = JSON.parse(cacheEntry);
            if (currentTime - timestamp > cacheDuration) {
              localStorage.removeItem(key);
            }
          }
        }
      }
    } catch (e) {
      console.error('Error clearing expired cache:', e);
    }
  }

  private startCacheCleanup() {
    // Run cleanup every 5 minutes (300,000 ms)
    if (typeof window !== 'undefined') {
      this.cacheCheckInterval = window.setInterval(() => {
        this.clearExpiredCache();
      }, 5 * 60 * 1000);
    }
  }

  stopCacheCleanup() {
    if (this.cacheCheckInterval !== null && typeof window !== 'undefined') {
      window.clearInterval(this.cacheCheckInterval);
      this.cacheCheckInterval = null;
    }
  }

  setNotiSearchResult(data: any) {
    this._notiSearchResult.next(data);
  }

  getNotiSearchResult() {
    return this.notiSearchResult$;
  }
}
